import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  initializePurchases,
  getProStatus,
  purchasePro,
  restorePurchases,
  getProductInfo,
  checkOwnership,
} from '../services/purchaseService';

// Pro features list
export const PRO_FEATURES = {
  PDF_IMPORT: 'pdf_import',
  PDF_EXPORT: 'pdf_export',
  HVAC_CALCULATIONS: 'hvac_calculations',
  MATERIAL_CALCULATOR: 'material_calculator',
  ELEVATION_VIEW: 'elevation_view',
  UNLIMITED_PROJECTS: 'unlimited_projects',
  NO_WATERMARK: 'no_watermark',
  ALL_HATCH_PATTERNS: 'all_hatch_patterns',
  ESTIMATES: 'estimates',
  INVOICES: 'invoices',
  COMPANY_PROFILE: 'company_profile',
};

// Free tier limits
export const FREE_LIMITS = {
  MAX_PROJECTS: 2,
  ALLOWED_HATCH_PATTERNS: ['none', 'diagonal', 'horizontal'],
};

// Context
const ProContext = createContext(null);

/**
 * Provider component for pro status
 */
export function ProProvider({ children }) {
  const [isPro, setIsPro] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [productInfo, setProductInfo] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        // Check stored pro status first
        const storedStatus = getProStatus();
        setIsPro(storedStatus);

        // Initialize purchase system
        await initializePurchases();

        // Check ownership (restores purchases if needed)
        const owned = await checkOwnership();
        setIsPro(owned);

        // Get product info
        setProductInfo(getProductInfo());
      } catch {
        // Silently handle init errors
      } finally {
        setIsLoading(false);
      }
    };

    init();

    // Listen for pro status changes
    const handleProChange = (e) => {
      setIsPro(e.detail.isPro);
    };
    window.addEventListener('proStatusChanged', handleProChange);

    return () => {
      window.removeEventListener('proStatusChanged', handleProChange);
    };
  }, []);

  // Purchase pro
  const purchase = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await purchasePro();
      if (result.success) {
        setIsPro(true);
        setShowUpgradeModal(false);
      }
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Restore purchases
  const restore = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await restorePurchases();
      if (result.success) {
        setIsPro(true);
      }
      return result;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check if a feature is available
  const hasFeature = useCallback((feature) => {
    if (isPro) return true;

    // Free features
    const freeFeatures = [];
    return freeFeatures.includes(feature);
  }, [isPro]);

  // Require a pro feature (shows upgrade modal if not pro)
  const requirePro = useCallback((feature, featureName) => {
    if (isPro) return true;

    setUpgradeFeature(featureName || feature);
    setShowUpgradeModal(true);
    return false;
  }, [isPro]);

  // Check project limit
  const canCreateProject = useCallback((currentProjectCount) => {
    if (isPro) return true;
    return currentProjectCount < FREE_LIMITS.MAX_PROJECTS;
  }, [isPro]);

  // Check hatch pattern availability
  const isHatchPatternAllowed = useCallback((patternId) => {
    if (isPro) return true;
    return FREE_LIMITS.ALLOWED_HATCH_PATTERNS.includes(patternId);
  }, [isPro]);

  const value = {
    isPro,
    isLoading,
    productInfo,
    showUpgradeModal,
    setShowUpgradeModal,
    upgradeFeature,
    purchase,
    restore,
    hasFeature,
    requirePro,
    canCreateProject,
    isHatchPatternAllowed,
    PRO_FEATURES,
    FREE_LIMITS,
  };

  return (
    <ProContext.Provider value={value}>
      {children}
    </ProContext.Provider>
  );
}

/**
 * Hook to access pro status
 */
export function useProStatus() {
  const context = useContext(ProContext);
  if (!context) {
    throw new Error('useProStatus must be used within a ProProvider');
  }
  return context;
}

export default useProStatus;
